'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon } from '@repo/ui';
import { Search } from '@repo/ui/icons';
import { cva } from 'class-variance-authority';
import { Popover as RadixPopover } from 'radix-ui';
import React, { type CSSProperties } from 'react';
import { SearchMenuTable } from './SearchMenuTable';

/**
 * SearchMenu content variants - Tailwind replica of dapp-ui-v5 StyledSearchMenuContent
 * (which extended the v5 Popover.Content base styles).
 */
const searchMenuContentVariants = cva([
// Layout (default width 300px, overridable via the width prop -> CSS variable)
'w-(--search-menu-width)', 'overflow-hidden', 'z-60',
// Visual
'bg-popover', 'rounded-lg', 'border-[0.5px] border-border', 'shadow-xl',
// Animations
'animate-fade-in-fast', 'data-[side=top]:animate-slide-up-and-fade', 'data-[side=right]:animate-slide-left-and-fade', 'data-[side=bottom]:animate-slide-down-and-fade', 'data-[side=left]:animate-slide-right-and-fade',
// Responsive: full-ish width, centered in the viewport below the `lg` breakpoint (1024px)
'max-lg:w-[90vw]', 'max-lg:mx-[calc((var(--radix-popover-content-available-width)-90vw)/2)]']);
interface SearchMenuContentStyle extends CSSProperties {
  '--search-menu-width'?: string;
}
export interface SearchMenuContentProps extends RadixPopover.PopoverContentProps {
  width?: number | string;
}
const Content = React.forwardRef<HTMLDivElement, SearchMenuContentProps>((t0, ref) => {
  const $ = _c(17);
  let children;
  let className;
  let props;
  let style;
  let width;
  if ($[0] !== t0) {
    ({
      children,
      className,
      style,
      width,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = style;
    $[5] = width;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    style = $[4];
    width = $[5];
  }
  const t1 = typeof width === "number" ? `${width}px` : width ?? "300px";
  let t2;
  if ($[6] !== style || $[7] !== t1) {
    t2 = {
      "--search-menu-width": t1,
      ...style
    };
    $[6] = style;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const contentStyle = t2;
  let t3;
  if ($[9] !== className) {
    t3 = cn(searchMenuContentVariants(), className);
    $[9] = className;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children || $[12] !== contentStyle || $[13] !== props || $[14] !== ref || $[15] !== t3) {
    t4 = <RadixPopover.Content ref={ref} side="top" sideOffset={8} {...props} className={t3} style={contentStyle} data-side={props.side}>{children}</RadixPopover.Content>;
    $[11] = children;
    $[12] = contentStyle;
    $[13] = props;
    $[14] = ref;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
});
Content.displayName = 'SearchMenu.Content';

/**
 * SearchMenu trigger - forces `asChild` so the consumer's element is the trigger
 * (matches the former dapp-ui-v5 `Popover.Trigger` behaviour).
 */
const Trigger = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <RadixPopover.Trigger asChild={true}>{children}</RadixPopover.Trigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
Trigger.displayName = 'SearchMenu.Trigger';

/**
 * SearchBar input styles - Tailwind replica of dapp-ui-v5 StyledSearchInput.
 */
const searchBarInputStyles = cn(['w-full flex-1 border-none bg-transparent', 'text-md text-foreground truncate leading-[normal]', 'placeholder:text-muted-foreground placeholder:leading-[inherit]', 'focus:outline-none', 'disabled:cursor-not-allowed disabled:text-muted-foreground']);
const SearchBar = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((t0, ref) => {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    const {
      children,
      className: t1,
      ...t2
    } = t0;
    className = t1;
    props = t2;
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={<Search />} className="text-secondary-foreground size-5" />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== className) {
    t2 = cn(searchBarInputStyles, className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== ref || $[8] !== t2) {
    t3 = <div className="p-md relative flex h-[66px] w-full items-center"><div className="gap-2xs flex w-full items-center justify-center">{t1}<input {...props} ref={ref} className={t2} /></div></div>;
    $[6] = props;
    $[7] = ref;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
});
SearchBar.displayName = 'SearchMenu.SearchBar';
const Separator = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    const {
      children,
      className: t1,
      ...t2
    } = t0;
    className = t1;
    props = t2;
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-border border-b-[0.5px] h-px", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
Separator.displayName = 'SearchMenu.Separator';

/**
 * SearchMenu component - Tailwind replica of dapp-ui-v5 SearchMenu.
 *
 * A popover-based search menu composed of a trigger, a search bar and a
 * sortable results table (SearchMenu.Table).
 */
export const SearchMenu = {
  Root: RadixPopover.Root,
  Content,
  SearchBar,
  Separator,
  Portal: RadixPopover.Portal,
  Trigger,
  Table: SearchMenuTable
};